import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { LogOut, ShieldCheck, Users, Crown, User, Search, X, RefreshCw, Inbox, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { Modal } from '../../components/ui/UI';
import './CpanelPage.css';

const isSuper = (a) => a.role === 'superadmin';
const initials = (n = '') => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'superadmin', label: 'Superadmins' },
  { key: 'admin', label: 'Admins' },
];

export default function CpanelPage() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cpanel/admins');
      setAdmins(data);
      setError('');
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not load administrators.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const stats = useMemo(() => {
    const sup = admins.filter(isSuper).length;
    return { total: admins.length, sup, adm: admins.length - sup };
  }, [admins]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter((a) => {
      if (tab === 'superadmin' && !isSuper(a)) return false;
      if (tab === 'admin' && isSuper(a)) return false;
      return !q || `${a.fullName} ${a.email}`.toLowerCase().includes(q);
    });
  }, [admins, tab, search]);

  const changeRole = async () => {
    if (!target) return;
    const role = isSuper(target) ? 'admin' : 'superadmin';
    setSaving(true);
    try {
      const { data } = await api.patch(`/cpanel/admins/${target._id}/role`, { role });
      setAdmins((cur) => cur.map((x) => (x._id === data._id ? data : x)));
      toast.success(`${data.fullName} is now ${role === 'superadmin' ? 'a superadmin' : 'an admin'}`);
      setTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update role. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem('hse_cpanel_token');
    navigate('/cpanel/login');
  };

  const promoting = target && !isSuper(target);
  const cards = [
    { icon: Users, label: 'Accounts', value: stats.total, tone: 'ink' },
    { icon: Crown, label: 'Superadmins', value: stats.sup, tone: 'safe' },
    { icon: User, label: 'Admins', value: stats.adm, tone: 'ink' },
  ];

  return (
    <main className="cp">
      <header className="cp-head">
        <div className="cp-brand">
          <span className="cp-mark"><ShieldCheck size={20} /></span>
          <div>
            <h1>Control Panel</h1>
            <p>UFL HSE · Manage who has superadmin access.</p>
          </div>
        </div>
        <div className="cp-actions">
          <button className="btn btn--ghost" onClick={fetchAdmins} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn--ghost" onClick={signOut}><LogOut size={15} /> Sign out</button>
        </div>
      </header>

      <section className="stats" aria-label="Account summary">
        {cards.map(({ icon: Icon, label, value, tone }) => (
          <div key={label} className={`stat stat--${tone}`}>
            <span className="stat-ic"><Icon size={18} /></span>
            <div>
              <div className="stat-val">{loading ? '–' : value}</div>
              <div className="stat-lbl">{label}</div>
            </div>
          </div>
        ))}
      </section>

      {error && (
        <div className="alert" role="alert">
          <AlertCircle size={17} /><span>{error}</span>
          <button className="link-btn" onClick={fetchAdmins}>Try again</button>
        </div>
      )}

      <section className="panel">
        <div className="bar">
          <div className="tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t.key} role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'tab tab--on' : 'tab'} onClick={() => setTab(t.key)}>
                {t.label}
                <span>{t.key === 'all' ? stats.total : t.key === 'superadmin' ? stats.sup : stats.adm}</span>
              </button>
            ))}
          </div>
          <div className="search">
            <Search size={15} />
            <input placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search administrators" />
            {search && <button onClick={() => setSearch('')} aria-label="Clear search"><X size={14} /></button>}
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th className="col-sn">S/N</th><th>Administrator</th><th>Email</th><th>Role</th><th>Last sign-in</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, r) => (
                  <tr key={r}>{Array.from({ length: 6 }).map((__, c) => <td key={c}><span className="skel" /></td>)}</tr>
                ))
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty">
                      <Inbox size={34} />
                      <h3>{admins.length ? 'No administrators match' : 'No administrators yet'}</h3>
                      <p>{admins.length ? 'Try a different search or role.' : 'Administrators appear here after they are invited.'}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                visible.map((a, i) => (
                  <tr key={a._id}>
                    <td className="col-sn">{i + 1}</td>
                    <td><div className="person"><span className={`avatar ${isSuper(a) ? 'avatar--super' : ''}`}>{initials(a.fullName)}</span><b>{a.fullName}</b></div></td>
                    <td className="col-muted">{a.email}</td>
                    <td><span className={`badge ${isSuper(a) ? 'badge--safe' : 'badge--neutral'}`}>{isSuper(a) ? 'Superadmin' : 'Admin'}</span></td>
                    <td>
                      {a.lastLogin ? (
                        <div className="login">
                          <span>{format(new Date(a.lastLogin), 'dd MMM yyyy, HH:mm')}</span>
                          <small>{formatDistanceToNow(new Date(a.lastLogin), { addSuffix: true })}</small>
                        </div>
                      ) : <span className="never">Never signed in</span>}
                    </td>
                    <td>
                      <button className={isSuper(a) ? 'btn-row btn-row--danger' : 'btn-row btn-row--ok'} onClick={() => setTarget(a)}>
                        {isSuper(a) ? 'Remove superadmin' : 'Make superadmin'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal open={!!target} onClose={() => !saving && setTarget(null)} title={promoting ? 'Make superadmin?' : 'Remove superadmin?'}>
        <div className="confirm">
          <p>
            {promoting
              ? <><b>{target?.fullName}</b> will be able to manage administrators and roles.</>
              : <><b>{target?.fullName}</b> will lose superadmin access and become a regular admin.</>}
          </p>
          <div className="confirm-btns">
            <button className="btn btn--ghost" onClick={() => setTarget(null)} disabled={saving}>Cancel</button>
            <button className={promoting ? 'btn btn--solid' : 'btn btn--danger'} onClick={changeRole} disabled={saving}>
              {saving ? 'Saving…' : promoting ? 'Make superadmin' : 'Remove access'}
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
}